import { DateTime } from 'luxon'
import { Flex, Select, Text } from '@radix-ui/themes'
import { useTranslation } from 'react-i18next'
import { DateRange } from '~/types'
import { useMemo, useState, useEffect } from 'react'
import { z } from 'zod'
import { FieldWrapper } from './FormFieldWrapper'
import DatePickerField from './DatePickerField'

export const SELECT_OPTIONS = {
  week: {
    label: 'dashboard.periods.week',
    value: {
      startDate: DateTime.now().minus({ days: 6 }).startOf('day'), // 7 days including today
      endDate: DateTime.now()
    }
  },
  month: {
    label: 'dashboard.periods.month',
    value: {
      startDate: DateTime.now().minus({ days: 29 }).startOf('day'), // 30 days including today
      endDate: DateTime.now()
    }
  },
  thisquarter: {
    label: 'dashboard.periods.thisquarter',
    value: {
      startDate: DateTime.now().startOf('quarter'),
      endDate: DateTime.now()
    }
  },
  thisyear: {
    label: 'dashboard.periods.thisyear',
    value: {
      startDate: DateTime.now().startOf('year'),
      endDate: DateTime.now()
    }
  },
  custom: {
    label: 'dashboard.periods.custom',
    value: {
      // Default custom range can be set via defaultPeriod prop
      startDate: DateTime.now(),
      endDate: DateTime.now()
    }
  }
}

export type PeriodSelection = keyof typeof SELECT_OPTIONS

// Define the type for default period selection in uncontrolled mode
export type DefaultPeriod =
  | {
      period: Exclude<PeriodSelection, 'custom'>
    }
  | {
      period: 'custom'
      dateRange: DateRange // Use DateRange for custom default
    }

export const periodSearchParamsSchema = z.union([
  z.object({
    period: z.literal('custom' satisfies PeriodSelection),
    from: z.string().date(),
    to: z.string().date()
  }),
  z.object({
    period: z
      .enum(['week', 'month', 'thisquarter', 'thisyear'] as const satisfies PeriodSelection[])
      .default('week')
  })
])

export type PeriodSearchQuery = z.infer<typeof periodSearchParamsSchema>

// --- Prop Types for Controlled vs Uncontrolled ---

// Base props excluding conflicting ones
type BasePeriodSelectorProps = Omit<Select.RootProps, 'onValueChange' | 'value' | 'defaultValue'>

// Props for controlled mode (value and onChange provided)
type ControlledPeriodSelectorProps = {
  periodSelection: PeriodSelection
  dateRange: DateRange
  onPeriodChange: (dateRange: DateRange, period: PeriodSelection) => void
  defaultPeriod?: never // Ensure defaultPeriod is not used in controlled mode
} & BasePeriodSelectorProps

// Props for uncontrolled mode (value and onChange are optional, defaultPeriod can be provided)
type UncontrolledPeriodSelectorProps = {
  periodSelection?: never // Ensure periodSelection is not used in uncontrolled mode
  dateRange?: never // Ensure dateRange is not used in uncontrolled mode
  onPeriodChange?: (dateRange: DateRange, period: PeriodSelection) => void // Optional callback
  defaultPeriod?: DefaultPeriod // Optional default state
} & BasePeriodSelectorProps

// Combined props type
type PeriodSelectorProps = ControlledPeriodSelectorProps | UncontrolledPeriodSelectorProps

// Helper to get initial state for uncontrolled mode
const getInitialState = (
  defaultPeriod?: DefaultPeriod
): { periodSelection: PeriodSelection; dateRange: DateRange } => {
  const defaultWeekValue = SELECT_OPTIONS.week.value
  if (!defaultPeriod) {
    return { periodSelection: 'week', dateRange: defaultWeekValue }
  }
  if (defaultPeriod.period === 'custom') {
    // Ensure start/end dates are valid DateTime objects
    return {
      periodSelection: 'custom',
      dateRange: {
        startDate:
          defaultPeriod.dateRange.startDate instanceof DateTime
            ? defaultPeriod.dateRange.startDate
            : DateTime.now(),
        endDate:
          defaultPeriod.dateRange.endDate instanceof DateTime ? defaultPeriod.dateRange.endDate : DateTime.now()
      }
    }
  }
  // Ensure the period exists in SELECT_OPTIONS
  const periodKey = defaultPeriod.period as Exclude<PeriodSelection, 'custom'>
  if (SELECT_OPTIONS[periodKey]) {
    return { periodSelection: periodKey, dateRange: SELECT_OPTIONS[periodKey].value }
  }
  // Fallback to default week if provided period is invalid
  return { periodSelection: 'week', dateRange: defaultWeekValue }
}

export const PeriodSelector = (props: PeriodSelectorProps) => {
  const { t } = useTranslation(['sites', 'common'])

  // Determine if controlled or uncontrolled based on presence of 'periodSelection' prop
  const isControlled = props.periodSelection !== undefined

  // Internal state for uncontrolled mode, initialized using defaultPeriod or 'week'
  const [internalState, setInternalState] = useState(() => getInitialState(props.defaultPeriod))

  // Get current values: from props if controlled, from state if uncontrolled
  const currentPeriodSelection = isControlled ? props.periodSelection : internalState.periodSelection
  const currentDateRange = isControlled ? props.dateRange : internalState.dateRange

  // Effect to potentially update internal state if defaultPeriod prop changes (uncontrolled mode)
  // Note: This makes defaultPeriod reactive. Remove if defaultPeriod should only set initial state.
  useEffect(() => {
    if (!isControlled && props.defaultPeriod) {
      setInternalState(getInitialState(props.defaultPeriod))
    }
    // Dependency array ensures this runs only when defaultPeriod changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.defaultPeriod])

  // Central handler for state changes
  const handleStateChange = (newDateRange: DateRange, newPeriod: PeriodSelection) => {
    // Call external callback (props.onPeriodChange) if provided, regardless of mode
    if (props.onPeriodChange) {
      props.onPeriodChange(newDateRange, newPeriod)
    }

    // Update internal state only if uncontrolled
    if (!isControlled) {
      setInternalState({ dateRange: newDateRange, periodSelection: newPeriod })
    }
  }

  // Handler for the Select dropdown change
  const handlePeriodSelectionChanged = (period: string) => {
    const newPeriodSelection = period as PeriodSelection // Cast string value to PeriodSelection
    // Use current date range if switching to 'custom', otherwise use predefined range
    const newDateRange =
      newPeriodSelection === 'custom' ? currentDateRange : SELECT_OPTIONS[newPeriodSelection].value
    handleStateChange(newDateRange, newPeriodSelection)
  }

  const handleDatesChanged = ({ from, to }: { from?: Date; to?: Date }) => {
    // Use current dates as fallback if 'from' or 'to' is null/undefined
    const startDate = from ? DateTime.fromJSDate(from).startOf('day') : currentDateRange.startDate
    const endDate = to ? DateTime.fromJSDate(to).endOf('day') : currentDateRange.endDate
    // When date picker changes, the period is always 'custom'
    handleStateChange({ startDate, endDate }, 'custom')
  }

  // Separate props for clarity and to avoid passing invalid combinations
  const {
    // Controlled props (used only for type checking and destructuring)
    periodSelection: _controlledPeriodSelection,
    dateRange: _controlledDateRange,
    // Uncontrolled props (used only for type checking and destructuring)
    defaultPeriod: _defaultPeriod,
    // Common props (onPeriodChange is handled by handleStateChange)
    onPeriodChange: _onPeriodChange,
    // Remaining props to pass down to Select.Root
    ...selectRootProps
  } = props

  const select = (
    <Select.Root
      size={'3'}
      // Value is always derived from the current state/props
      value={currentPeriodSelection}
      onValueChange={handlePeriodSelectionChanged}
      {...selectRootProps} // Pass down remaining Select props
    >
      <Select.Trigger>
        <Text size="2">{t(SELECT_OPTIONS[currentPeriodSelection].label)}</Text>
      </Select.Trigger>
      <Select.Content variant="soft" color="gray" position="popper">
        {Object.entries(SELECT_OPTIONS).map(([key, { label }]) => (
          <Select.Item key={key} value={key}>
            {t(label)}
          </Select.Item>
        ))}
      </Select.Content>
    </Select.Root>
  )

  if (currentPeriodSelection === 'custom') {
    return (
      <Flex gap="2" direction={'column'}>
        {select}
        <Flex gap={'3'}>
          <FieldWrapper label={t('startDate', { ns: 'common' })}>
            <DatePickerField
              value={currentDateRange.startDate.toJSDate()}
              onChange={(from) => handleDatesChanged({ from })}
            />
          </FieldWrapper>
          <FieldWrapper label={t('endDate', { ns: 'common' })}>
            <DatePickerField
              value={currentDateRange.endDate.toJSDate()}
              onChange={(to) => handleDatesChanged({ to })}
            />
          </FieldWrapper>
        </Flex>
      </Flex>
    )
  }

  return select
}

// ... (usePeriodSearchParams and useDefaultDateRange can remain as they are,
//      though useDefaultDateRange might be less needed if using the uncontrolled component)
// Helper hook to convert between search params and DateRange/PeriodSelection
export const usePeriodSearchParams = () => {
  // Convert from search params to DateTime objects
  const fromSearchParams = (
    searchQuery: PeriodSearchQuery
  ): { dateRange: DateRange; periodSelection: PeriodSelection } => {
    const periodSelection = searchQuery.period

    const dateRange =
      periodSelection === 'custom'
        ? {
            startDate: DateTime.fromISO(searchQuery.from),
            endDate: DateTime.fromISO(searchQuery.to)
          }
        : SELECT_OPTIONS[periodSelection].value

    return { dateRange, periodSelection }
  }

  // Convert from DateTime objects to search params
  const toSearchParams = (dateRange: DateRange, periodSelection: PeriodSelection): PeriodSearchQuery => {
    return periodSelection === 'custom'
      ? {
          period: 'custom',
          from: dateRange.startDate.toISODate()!,
          to: dateRange.endDate.toISODate()!
        }
      : { period: periodSelection }
  }

  return { fromSearchParams, toSearchParams }
}

// Helper hook for components that need a simple date range with default values
// This hook remains useful for getting default values outside the component context.
export const useDefaultDateRange = (defaultPeriod: PeriodSelection = 'week'): DateRange => {
  return useMemo(() => {
    // Handle potential 'custom' case if passed, though unlikely for this hook's purpose
    if (defaultPeriod === 'custom') {
      return SELECT_OPTIONS.week.value // Fallback for custom in this context
    }
    return SELECT_OPTIONS[defaultPeriod].value
  }, [defaultPeriod])
}
